import React, { useEffect, useState, useCallback } from "react";
import { get, compact } from "lodash";

import {
  IFormBlockOption,
  IFormComponentType,
  IFormItemChangeValue,
  IFormValue,
  AggregateFunctions,
  IAggregateFormValue,
} from "../../types/form-type";
import { ITableData } from "../../types/table-type";
import { AppType } from "../../types/global-type";

import { getChartFormConfig, getAggregateFormConfig } from "../../utils/form_main";
import { usePrevious } from "../../utils/hooks";
import { getColumn } from "../../utils/tableData_secondary";
import { getCalculationTypeOption } from "../../utils/form_secondary";

import Input from "./Form/Input";
import Select from "./Form/Select";
import SelectForTree from "./Form/SelectForTree";
import SelectForTab from "./Form/SelectForTab";
import Switch from "./Form/Switch";
import Title from "./Form/Title";
import FormBlock from "./Form/FormBlock";
import Filter from "./Form/Filter";
import ChartTypeSelect from "../ChartTypeSelect";

import { Container, ChildrenDiv, OuterContainer } from "./styled";

const COMPONENT_TO_TYPE: {
  [key in IFormComponentType]?: React.ComponentType<any>;
} = {
  [IFormComponentType.Input]: Input,
  [IFormComponentType.Select]: Select,
  [IFormComponentType.Switch]: Switch,
  [IFormComponentType.Title]: Title,
  [IFormComponentType.SelectForTree]: SelectForTree,
  [IFormComponentType.SelectForTab]: SelectForTab,
  [IFormComponentType.FormBlock]: FormBlock,
  [IFormComponentType.Filter]: Filter,
  [IFormComponentType.ChartTypeSelect]: ChartTypeSelect,
};

interface IProps {
  onChange: (value: IFormValue) => void;
  value: IFormValue;
  tableData: ITableData;
  appType: AppType;
  shareUserId: string;
}

const ChartConfig: React.FC<IProps> = ({ onChange, value, tableData, appType, shareUserId }) => {
  const [formConfig, setFormConfig] = useState<IFormBlockOption[]>([]);

  const prevValue = usePrevious(value);

  useEffect(() => {
    let config: IFormBlockOption[] = [];

    if (appType === AppType.CHART) {
      const { chartType } = value as IFormValue<AppType.CHART>;

      config = getChartFormConfig(tableData, value as IFormValue<AppType.CHART>, chartType, shareUserId);
    } else if (appType === AppType.AGGREGATE) {
      config = getAggregateFormConfig(tableData);
    }

    setFormConfig(config);
  }, [appType, value, tableData]);

  const handleChange = useCallback(
    (changeValue: IFormItemChangeValue) => {
      const newValue = {
        ...value,
        ...changeValue,
      };
      onChange(newValue);
    },
    [onChange, value]
  );

  // 逻辑补强 对于统计 当列改变时 计算函数的value重置 为 options[0]
  useEffect(() => {
    if (appType === AppType.AGGREGATE && value.calculationTargetId !== prevValue?.calculationTargetId && !!prevValue) {
      let calculationTypeStr: AggregateFunctions;
      const newColumn = getColumn(value.calculationTargetId || "", tableData);
      if (!!newColumn) {
        const newConditionOption = getCalculationTypeOption(value as IAggregateFormValue, tableData);
        if (newConditionOption.length !== 0) {
          calculationTypeStr = newConditionOption[0].value as AggregateFunctions;
        }
      }
      const newFormValue = {
        ...value,
        // @ts-ignore
        calculationType: calculationTypeStr,
      };
      onChange(newFormValue as IFormValue<AppType.AGGREGATE>);
    }
  }, [value.calculationTargetId, appType, onChange, value, prevValue?.calculationTargetId, tableData, prevValue]);

  if (!Array.isArray(formConfig) || formConfig.length === 0) return null;

  return (
    <OuterContainer>
      <Container appType={appType}>
        {formConfig.map((item) => {
          const { childrenOption } = item;

          const children = compact(
            childrenOption.map((item2) => {
              const { isShow, componentType, getOptions, options } = item2;

              if (isShow && !isShow(value)) return null;
              const Component = COMPONENT_TO_TYPE[componentType];

              if (!Component) return "";

              if (item2.componentType !== IFormComponentType.FormBlock) {
                return (
                  <Component
                    css={ChildrenDiv}
                    {...item2}
                    key={item2.id}
                    onChange={handleChange}
                    value={get(value, item2.id)}
                    options={getOptions ? getOptions(value, tableData) : options || []}
                  />
                );
              }
            })
          );

          return (
            <FormBlock
              {...item}
              key={item.id}
              value={get(value, item.id)}
              onChange={handleChange}
              children={children}
            />
          );
        })}
      </Container>
    </OuterContainer>
  );
};

export default ChartConfig;
